import type { ToolApproval } from "@/api/models"

/**
 * Deep Explorer tool approvals (#2962).
 *
 * When a Deep tool with an approval policy (create_project, create_saved_prompt,
 * send_email, ...) is called after the plan was approved, the backend emits a
 * `deep_tool_approval_required` frame on the Deep websocket and waits for a
 * `deep_tool_approval_decision` frame on the same socket. Until this module
 * existed the frontend had no handler for either, so every such call timed
 * out after `timeout_secs` and was denied — the "project tool is locked" /
 * "stuck in Pending" symptom from the 2026-09-09 protocols.
 *
 * The request is rendered through the ordinary `ToolApprovalOverlay` (the
 * same card ordinary-chat approvals use) by inserting a `ToolApproval` into
 * the session's chat state. Because the reply must go over the Deep socket
 * rather than the chat socket, the approval id is prefixed and this registry
 * keeps the sender for it; `useToolApprovalActions` hands prefixed ids here.
 */

const DEEP_TOOL_APPROVAL_PREFIX = "deep-tool:"

export type DeepToolApprovalRequest = {
  tool: string
  args: Record<string, unknown>
  summary?: string
  timeout_secs?: number
}

export type DeepSocketSender = (message: Record<string, unknown>) => void

/**
 * The live Deep socket sender per session, module-scoped on purpose: the
 * approval registry below outlives the Explorer hook instance (a remount
 * creates a new hook with fresh refs while the pending card survives), so
 * the lookup has to live where the registry lives, not in a hook ref.
 */
const socketSenders = new Map<string, DeepSocketSender>()

export function registerDeepSocketSender(sessionId: string, send: DeepSocketSender): void {
  socketSenders.set(sessionId, send)
}

/** Identity-guarded: a newer socket registered under the same session wins. */
export function unregisterDeepSocketSender(sessionId: string, send: DeepSocketSender): void {
  if (socketSenders.get(sessionId) === send) socketSenders.delete(sessionId)
}

export function getDeepSocketSender(sessionId?: string | null): DeepSocketSender | null {
  if (!sessionId) return null
  return socketSenders.get(sessionId) ?? null
}

type Registered = {
  sessionId: string
  frameSeq: number | null
  /** `_request_generation_id` of the frame that asked; a terminal frame only
   *  cancels the approvals of ITS request, never a newer run's (#2962). */
  generationId: string | null
  tool: string
  /** Looked up at decision time, never captured: a reattach replaces the
   *  session's Deep socket, and the answer must go over the live one. */
  resolveSend: () => DeepSocketSender | null | undefined
  onDecided: (status: ToolApproval["status"]) => void
  timer: ReturnType<typeof setTimeout> | null
}

const registry = new Map<string, Registered>()

export function isDeepToolApprovalId(requestId: string): boolean {
  return requestId.startsWith(DEEP_TOOL_APPROVAL_PREFIX)
}

function newRequestId(): string {
  const random =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`
  return `${DEEP_TOOL_APPROVAL_PREFIX}${random}`
}

/**
 * Register a pending Deep tool approval and return the `ToolApproval` to put
 * into chat state. `onDecided` is called with the final status once the user
 * decides or the backend's timeout elapses, so the caller can update the
 * card; it is never called twice.
 */
export function registerDeepToolApproval(params: {
  sessionId: string
  request: DeepToolApprovalRequest
  /** The websocket frame's `_seq`. The Deep socket can deliver one frame to
   *  more than one subscriber; the same (session, seq) registers once. */
  frameSeq?: number | null
  /** The request frame's `_request_generation_id`; scopes turn-end cancels. */
  generationId?: string | null
  /** Resolves the session's CURRENT Deep socket sender. Called when the user
   *  decides, not now: the socket that delivered the request may be gone by
   *  then (page reload + reattach) and its replacement must carry the reply.
   *  Defaults to the module-level sender registry above. */
  resolveSend?: () => DeepSocketSender | null | undefined
  onDecided: (requestId: string, status: ToolApproval["status"]) => void
  now?: () => number
}): ToolApproval | null {
  const frameSeq = typeof params.frameSeq === "number" ? params.frameSeq : null
  if (frameSeq !== null) {
    for (const entry of registry.values()) {
      if (entry.sessionId === params.sessionId && entry.frameSeq === frameSeq) {
        return null
      }
    }
  }
  const now = params.now ?? Date.now
  const requestId = newRequestId()
  const createdAt = new Date(now()).toISOString()
  const timeoutSecs =
    typeof params.request.timeout_secs === "number" && params.request.timeout_secs > 0
      ? params.request.timeout_secs
      : null
  const summary = params.request.summary?.trim() || `Approve calling ${params.request.tool}?`

  const entry: Registered = {
    sessionId: params.sessionId,
    frameSeq,
    generationId: params.generationId ?? null,
    tool: params.request.tool,
    resolveSend: params.resolveSend ?? (() => getDeepSocketSender(params.sessionId)),
    onDecided: (status) => params.onDecided(requestId, status),
    timer: null,
  }
  if (timeoutSecs !== null) {
    // The backend denies on its own after the timeout; mirror that so a card
    // nobody answered does not sit there forever pretending to be actionable.
    entry.timer = setTimeout(() => {
      if (!registry.has(requestId)) return
      registry.delete(requestId)
      entry.onDecided("expired")
    }, timeoutSecs * 1000)
  }
  registry.set(requestId, entry)

  return {
    request_id: requestId,
    call_id: requestId,
    tool: params.request.tool,
    title: summary,
    summary,
    arguments: params.request.args,
    status: "pending",
    created_at: createdAt,
    updated_at: createdAt,
    timeout_secs: timeoutSecs,
  }
}

/**
 * Answer a registered Deep tool approval. Returns false when the id is not a
 * Deep approval (the caller then uses the ordinary chat-socket path). Throws
 * when the session has no live Deep socket right now — the card stays
 * pending (a reattach can still answer it) and the caller surfaces the
 * failure instead of the card claiming "approved" against nobody.
 */
export function decideDeepToolApproval(requestId: string, approved: boolean): boolean {
  if (!isDeepToolApprovalId(requestId)) return false
  const entry = registry.get(requestId)
  if (!entry) {
    // Already decided or expired — nothing to send, but the id was ours.
    return true
  }
  const send = entry.resolveSend()
  if (!send) {
    throw new Error("Deep Explorer connection is not available; the approval is still pending")
  }
  send({
    type: "deep_tool_approval_decision",
    tool: entry.tool,
    decision: approved ? "approve" : "deny",
  })
  if (entry.timer) clearTimeout(entry.timer)
  registry.delete(requestId)
  entry.onDecided(approved ? "approved" : "denied")
  return true
}

/**
 * Drop the pending approvals of a session's turn (it ended or was cancelled).
 * `generationId` scopes the cancel to the request that actually ended: a late
 * terminal frame from an earlier socket must not clear the cards of a newer
 * live run on the same session. Without a generation on either side every
 * approval of the session is dropped.
 */
export function cancelDeepToolApprovalsForSession(
  sessionId: string,
  generationId?: string | null
): void {
  for (const [requestId, entry] of Array.from(registry.entries())) {
    if (entry.sessionId !== sessionId) continue
    if (generationId && entry.generationId && entry.generationId !== generationId) continue
    if (entry.timer) clearTimeout(entry.timer)
    registry.delete(requestId)
    entry.onDecided("cancelled")
  }
}

/** Test hook. */
export function resetDeepToolApprovalsForTests(): void {
  for (const entry of registry.values()) {
    if (entry.timer) clearTimeout(entry.timer)
  }
  registry.clear()
  socketSenders.clear()
}
